import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

import cogoToast from 'cogo-toast'

import { addNotification } from '../actions/notificationActions'
import { getToken } from '../utils/getToken'
import { jwtDecode } from '../utils/jwtDecode'

const useServerSentEvents = () => {
  const dispatch = useDispatch()
  const eventSourceRef = useRef(null)

  const token = getToken()

  useEffect(() => {
    if (!token) {
      console.log('[SSE] No token available. Skipping connection.')
      return
    }

    let retryCount = 0
    const MAX_RETRIES = 5

    const connect = () => {
      // 1. Check if token is expired
      const decoded = jwtDecode(token)
      if (decoded && decoded.exp) {
        const currentTime = Date.now() / 1000
        if (decoded.exp < currentTime) {
          console.warn('[SSE] Token expired. Aborting connection.', { exp: decoded.exp, now: currentTime })
          return
        }
      }

      // EventSource doesn't support headers natively in browser API (for Bearer token).
      // However, we can use a query param `access_token` since our Program.cs handles it.
      // Check Program.cs lines 121-133: it reads access_token from query if header is missing.

      console.log('[SSE] Connecting with token:', token.substring(0, 10) + '... (Length: ' + token.length + ')')
      const url = `/api/notifications/stream?access_token=${token}`

      const eventSource = new EventSource(url)
      eventSourceRef.current = eventSource

      eventSource.onopen = () => {
        console.log('[SSE] Connected')
        retryCount = 0 // reset on successful connection
      }

      eventSource.onmessage = event => {
        // Keep-alive pings or general messages
        if (event.data === ': connected' || event.data === ': ping') {
          return
        }

        try {
          const data = JSON.parse(event.data)

          if (data.chatThreadId && data.content) {
            // It's a chat message - dispatch custom event for ChatRoom to pick up
            const customEvent = new CustomEvent('chat-message', { detail: data })
            window.dispatchEvent(customEvent)
          } else {
            // Assume it's a notification
            const customEvent = new CustomEvent('app-notification', { detail: data })
            window.dispatchEvent(customEvent)

            dispatch(addNotification(data))

            // Show immediate toast alert to user
            const toastHeading = data.title || data.Title || ''
            const toastBody =
              data.body || data.Body || data.message || data.Message || data.content || data.Content || ''

            if (toastHeading || toastBody) {
              const toastMsg = toastBody || toastHeading || 'Notification'
              const toastOpts = {
                heading: toastHeading && toastHeading !== toastMsg ? toastHeading : undefined,
                position: 'top-right',
                hideAfter: 8,
                onClick: () => {
                  const link = data.deepLink || data.DeepLink
                  if (link) {
                    window.location.href = link
                  }
                },
              }
              const lowerTitle = (toastHeading || toastMsg).toLowerCase()
              if (lowerTitle.includes('complete') || lowerTitle.includes('success')) {
                cogoToast.success(toastMsg, toastOpts)
              } else if (lowerTitle.includes('failed') || lowerTitle.includes('error')) {
                cogoToast.error(toastMsg, toastOpts)
              } else {
                cogoToast.info(toastMsg, toastOpts)
              }
            }
          }
        } catch (e) {
          console.error('[SSE] Error parsing data', e)
        }
      }

      eventSource.onerror = err => {
        console.error('[SSE] Error', err)
        eventSource.close()

        // NOTE: EventSource.onerror does NOT expose HTTP status codes — err.status is always
        // undefined in the browser API. Auth failures appear as generic errors here.
        // We detect them via token expiry instead (see below).

        // Check if the current token is expired or within 30s of expiring.
        // If so, the handleExpiring handler in Auth.js will refresh it shortly.
        // We defer the reconnect rather than retrying immediately with a bad token.
        const currentToken = getToken()
        const currentDecoded = currentToken ? jwtDecode(currentToken) : null
        const tokenExpiresAt = currentDecoded?.exp ?? 0
        const nowSec = Date.now() / 1000
        const tokenIsExpiredOrExpiring = tokenExpiresAt - nowSec < 30 // within 30s of expiry

        if (tokenIsExpiredOrExpiring) {
          // Wait for Auth.js to refresh the token (fires userLoaded which changes the `token`
          // value and re-runs this effect with a fresh connection). Cap at 10s in case the
          // refresh itself fails and the user will be redirected to sign-in by Auth.js.
          const waitMs = Math.max(0, (tokenExpiresAt - nowSec + 5) * 1000)
          const clampedWait = Math.min(waitMs, 10000)
          console.log(
            `[SSE] Token expired or expiring in <30s. Deferring reconnect ${(clampedWait / 1000).toFixed(
              1,
            )}s to await refresh.`,
          )
          setTimeout(() => {
            if (eventSourceRef.current === eventSource) {
              connect()
            }
          }, clampedWait)
          return
        }

        // Token is valid — the error was a transient network/server issue.
        // Cap retries to avoid spamming when the API container is cold/down.
        // Exponential backoff: 5s, 10s, 20s, 40s, 60s — then stop.
        if (retryCount >= MAX_RETRIES) {
          console.warn(
            `[SSE] Max retries (${MAX_RETRIES}) reached. Stopping reconnection loop. API may be cold — will retry on next mount.`,
          )
          return
        }

        retryCount++
        const backoffMs = Math.min(5000 * Math.pow(2, retryCount - 1), 60000)
        console.log(`[SSE] Retrying connection in ${backoffMs / 1000}s (attempt ${retryCount}/${MAX_RETRIES})...`)

        setTimeout(() => {
          if (eventSourceRef.current === eventSource) {
            connect()
          }
        }, backoffMs)
      }
    }

    connect()

    return () => {
      if (eventSourceRef.current) {
        console.log('[SSE] Closing connection due to cleanup (token change or unmount)')
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }, [dispatch, token])
}

export default useServerSentEvents
